import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { formatNumber, NgClass } from "@angular/common";
import { forkJoin } from "rxjs";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { HomeService } from "../home/home.service";
import { HomeControllerService } from "../../../services/server/HomeController.service";
import { AlarmControllerService } from "../../../services/server/AlarmController.service";
import { DateParserService } from "../../../services/shared/DateParserService.service";
import { Client } from "../../../interfaces/ClientGlobalInterface.type";
import { DeviceTypes, GlobalEntityData, HomeAlarmData } from "../home/HomeInterface.type";
import { Options } from "highcharts";
import { Entity } from "../../../interfaces/EntityGlobalInterface.type";
import { HomeEntityCardComponent } from "./home-entity-card.component/home-entity-card.component";
import { MaterialSelectComponent } from "../../../modules/material-module/material-select/material-select.component";
import { HomeEntityDataRowComponent } from "./home-entity-data-row.component/home-entity-data-row.component";
import { MatRadioModule } from "@angular/material/radio";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { CountrySelectComponent } from "@wlucha/ng-country-select";

/**
 * Client home listing every entity as a card or data row with device totals and alarms.
 * Supports text search, country filter, alarm filter and ordering by entity, meters or alarms.
 */
@Component({
  selector: "app-home-entities",
  imports: [
    TranslateModule,
    HomeEntityCardComponent,
    HomeEntityDataRowComponent,
    MaterialSelectComponent,
    MatRadioModule,
    FormsModule,
    MatInputModule,
    NgClass,
    CountrySelectComponent
  ],
  templateUrl: "./home-entities.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./home-entities.component.scss"
})
export class HomeEntitiesComponent implements OnInit, AfterViewInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Session data stream
  currentClient: Client;
  entityList: Entity[];
  numberFormat: string;

  // Entity array
  homeGlobalEntities: GlobalEntityData[];
  globalEntity: GlobalEntityData;
  @ViewChild("container") container: ElementRef;
  globalCardHeight: number;
  mainAlarmList: HomeAlarmData[];
  filterAlarmList: HomeAlarmData[];

  // Charts
  deviceTotalsHighchartsOptions: any;
  alarmsCloudHighchartsOptions: Options;
  deviceTypeHighchartsOptions: Options;
  networkHighchartsOptions: Options;
  formatNumber = formatNumber;

  // Filters
  alarmFilter: HomeAlarmData[];
  orderByData = [
    { id: "entity", name: this.translate.instant("entity") },
    { id: "meters", name: this.translate.instant("meters") },
    { id: "alarms", name: this.translate.instant("alarms") }
  ];
  orderBy: string = "entity";
  reverseOrder: boolean = false;
  searchEntity: string;
  countryList: string[];
  selectedCountry: string;
  countrySelectorLang: string;
  showAllEntities: boolean = true;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly AlarmController: AlarmControllerService,
    private readonly DateParserService: DateParserService,
    private readonly HomeController: HomeControllerService,
    public HomeService: HomeService,
    private readonly SessionDataService: SessionDataService,
    private readonly spinner: NgxSpinnerService,
    private readonly translate: TranslateService
  ) {
    effect(() => {
      const entityList = this.SessionDataService.entityList();
      if (entityList !== null) {
        this.entityList = entityList;
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.currentClient = untracked(() => this.SessionDataService.client());
    this.numberFormat = untracked(() => this.SessionDataService.numberFormat());
    this.entityList = untracked(() => this.SessionDataService.entityList());
    let currentLanguage = untracked(() => this.SessionDataService.language());
    this.countrySelectorLang = ["en", "de", "fr", "es", "it", "pt"].includes(currentLanguage)
      ? currentLanguage
      : "en";

    if (this.currentClient) {
      this.loadComponent();
    }
  }

  /***************************************************************************/
  // ANCHOR After-render execution
  /***************************************************************************/

  ngAfterViewInit(): void {
    this.globalCardHeight = this.container.nativeElement.offsetHeight;
  }

  /***************************************************************************/
  // ANCHOR Component teardown
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  loadComponent(): void {
    this.getEntities();
  }

  getEntities(): void {
    let entities: GlobalEntityData[] = [];

    this.HomeController.getGlobalEntityData(this.currentClient.clientId).subscribe(response => {
      if (response["code"] === 0) {
        entities = response["body"];
        entities.forEach(entity => {
          let clientEntity = this.currentClient.entityList.find(
            clientEntity => clientEntity.id === entity.id
          );
          let entityVirtual = clientEntity.agrupations.find(agrupation => agrupation.virtual);
          entity.virtual = entityVirtual || clientEntity.agrupations[0];
          entity.show = true;
          entity.hidden = false;
          entity.devicesCountByType = this.parseDeviceTypes(entity);
          entity.hasDevices =
            entity.devicesCountByType.gateways.total > 0 ||
            entity.devicesCountByType.meters.total > 0 ||
            entity.devicesCountByType.external.total > 0 ||
            entity.devicesCountByType.externalWithoutCom.noCom > 0 ||
            entity.devicesCountByType.mbus.total > 0 ||
            entity.devicesCountByType.concentrators.total > 0;
          this.getEntityCharts(entity);
        });
        entities.sort((a, b) => a.entity.localeCompare(b.entity));
        this.homeGlobalEntities = entities;
        this.getCountryList();
        this.getGlobalData();
        this.getEntitiesAlarms();
      }
    });
  }

  getCountryList(): void {
    let countryList = [];
    this.selectedCountry = null;
    this.homeGlobalEntities.forEach(globalEntity => {
      if (globalEntity.hasDevices) {
        let entity = this.entityList.find(entity => entity.id === globalEntity.id);
        if (entity.country && !countryList.includes(entity.country)) {
          countryList.push(entity.country);
        }
      }
    });
    this.countryList = countryList;
  }

  getEntityCharts(entity: GlobalEntityData): void {
    entity.devicesChartOptions = this.HomeService.setDevicesChartsOptions(
      entity.devicesCountByType
    );
    entity.networkChartOptions = this.HomeService.setDevicesChartsOptions(
      entity.devicesCountByType,
      true
    );
    let totalChart = this.HomeService.setDeviceTotalsChartsOptions(
      entity.devicesCountByType,
      null,
      window.innerWidth > 980
        ? 380
        : window.innerWidth -
            2.5 * Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
    );
    entity.totalPending = totalChart.total.pending;
    entity.totalInNetwork = totalChart.total.com;
    entity.totalNoCommunication = totalChart.total.noCom;
    entity.deviceTotalsChartOptions = totalChart.chartOptions;
    entity.devicesTotal = 0;
    let excludedTypes = new Set(["gateways", "txn", "concentrators"]);
    for (let type in entity.devicesCountByType) {
      if (!excludedTypes.has(type)) {
        entity.devicesTotal += entity.devicesCountByType[type].total;
      }
    }
  }

  getEntitiesAlarms(): void {
    let requests = this.homeGlobalEntities.map(entity => {
      return this.AlarmController.getEntityActiveEnabledAlarms(entity.id);
    });
    forkJoin(requests).subscribe(responses => {
      responses.forEach((response, i) => {
        if (response["code"] === 0) {
          let meterAlarms = response["body"];
          let alarmCodeList: HomeAlarmData[] = meterAlarms.map(alarm => {
            return {
              alarmCode: alarm.code,
              total: alarm.count,
              name: this.translate.instant("_alert_meter." + alarm.code)
            };
          });
          this.homeGlobalEntities[i].alarmCodeList = alarmCodeList;
          this.homeGlobalEntities[i].totalAlarms = alarmCodeList.reduce(
            (total, a) => total + a.total,
            0
          );
          this.homeGlobalEntities[i].alarmsCloudChartOptions =
            this.HomeService.setAlarmsCloudChartsOptions(alarmCodeList, true);
        }
      });
      this.getGlobalAlarms(true);
    });
  }

  // Parse device totals for charts
  parseDeviceTypes(entity: GlobalEntityData): DeviceTypes {
    let devicesCountByType = new DeviceTypes();
    // Gateways
    devicesCountByType.gateways.total = entity.stateValues.gateways;
    devicesCountByType.gateways.com = entity.stateValues.networkGateways;
    devicesCountByType.gateways.noCom = entity.stateValues.noCommunicateGateways;
    // LW
    devicesCountByType.meters.total = entity.stateValues.lwMeters;
    devicesCountByType.meters.com = entity.stateValues.lwNetworkCommunicationMeters;
    devicesCountByType.meters.noCom = entity.stateValues.lwNoCommMeters;
    devicesCountByType.meters.noAssigned = entity.stateValues.lwNoNetworkMeters;
    // MBUS
    devicesCountByType.mbus.total = entity.stateValues.mbusMeters;
    devicesCountByType.mbus.com = entity.stateValues.mbusNetworkCommunicateMeters;
    devicesCountByType.mbus.noCom = entity.stateValues.mbusNoCommNoNetworkMeters;
    // External
    devicesCountByType.external.total = entity.stateValues.externalMeters;
    devicesCountByType.external.com = entity.stateValues.externalNetworkCommunicationMeters;
    devicesCountByType.external.noCom = entity.stateValues.externalNoCommMeters;
    // LW MBUS
    devicesCountByType.concentrators.total = entity.stateValues.lorawanMbus;
    devicesCountByType.concentrators.com = entity.stateValues.networkLorawanMbus;
    devicesCountByType.concentrators.noCom = entity.stateValues.noCommunicateLorawanMbus;
    // External without communication
    devicesCountByType.externalWithoutCom.total = entity.stateValues.externalWithoutCom;
    devicesCountByType.externalWithoutCom.noCom = entity.stateValues.externalWithoutCom;

    return devicesCountByType;
  }

  getGlobalData(): void {
    let globalEntity: GlobalEntityData = {
      id: null,
      entity: this.homeGlobalEntities.some(entity => !entity.show || entity.hidden)
        ? this.translate.instant("selection")
        : this.translate.instant("global")
    };
    let devicesCountByType = new DeviceTypes();
    this.homeGlobalEntities.forEach(entity => {
      if (entity.show && !entity.hidden) {
        // Gateways
        devicesCountByType.gateways.total += entity.stateValues.gateways;
        devicesCountByType.gateways.com += entity.stateValues.networkGateways;
        devicesCountByType.gateways.noCom += entity.stateValues.noCommunicateGateways;
        // LW
        devicesCountByType.meters.total += entity.stateValues.lwMeters;
        devicesCountByType.meters.com += entity.stateValues.lwNetworkCommunicationMeters;
        devicesCountByType.meters.noCom += entity.stateValues.lwNoCommMeters;
        devicesCountByType.meters.noAssigned += entity.stateValues.lwNoNetworkMeters;
        // MBUS
        devicesCountByType.mbus.total += entity.stateValues.mbusMeters;
        devicesCountByType.mbus.com += entity.stateValues.mbusNetworkCommunicateMeters;
        devicesCountByType.mbus.noCom += entity.stateValues.mbusNoCommNoNetworkMeters;
        // External
        devicesCountByType.external.total += entity.stateValues.externalMeters;
        devicesCountByType.external.com += entity.stateValues.externalNetworkCommunicationMeters;
        devicesCountByType.external.noCom += entity.stateValues.externalNoCommMeters;
        // LW MBUS
        devicesCountByType.concentrators.total += entity.stateValues.lorawanMbus;
        devicesCountByType.concentrators.com += entity.stateValues.networkLorawanMbus;
        devicesCountByType.concentrators.noCom += entity.stateValues.noCommunicateLorawanMbus;
        // External without communication
        devicesCountByType.externalWithoutCom.total += entity.stateValues.externalWithoutCom;
        devicesCountByType.externalWithoutCom.noCom += entity.stateValues.externalWithoutCom;
      }
    });
    globalEntity.devicesCountByType = devicesCountByType;
    this.getEntityCharts(globalEntity);
    this.globalEntity = globalEntity;
  }

  getGlobalAlarms(loadAlarmFilter?: boolean): void {
    let alarmCodeList = [];
    let entities = JSON.parse(JSON.stringify(this.homeGlobalEntities));
    let showedEntities = entities.filter(
      entity => entity.alarmCodeList?.length > 0 && entity.show && !entity.hidden
    );
    let allAlarms = showedEntities
      .map(entity => entity.alarmCodeList)
      .reduce((a, b) => {
        return a.concat(b);
      }, []);

    allAlarms.forEach(alarm => {
      let alarmAddded = alarmCodeList.find(
        alarmAdded => alarmAdded.alarmCode === alarm.alarmCode || alarmAdded.name === alarm.name
      );
      if (alarmAddded) {
        alarmAddded.total += alarm.total;
      } else {
        alarmCodeList.push(alarm);
      }
    });

    alarmCodeList.forEach(alarm => {
      alarm.filter = alarm.name + " (" + alarm.total + ")";
      alarm.desc = this.translate.instant("_alert_description." + alarm.alarmCode);
    });
    alarmCodeList.sort((a, b) => b.total - a.total);
    this.globalEntity.alarmCodeList = alarmCodeList;
    this.globalEntity.totalAlarms = showedEntities.reduce(
      (total, entity) => total + entity.totalAlarms,
      0
    );
    this.mainAlarmList = this.globalEntity.alarmCodeList.filter(
      alarm => alarm.total / this.globalEntity.totalAlarms >= 0.05
    );
    this.mainAlarmList.sort((a, b) => b.total - a.total);
    this.globalEntity.alarmsCloudChartOptions = this.HomeService.setAlarmsCloudChartsOptions(
      alarmCodeList,
      true
    );
    if (loadAlarmFilter) this.filterAlarmList = JSON.parse(JSON.stringify(alarmCodeList));
  }

  updateFilters(avoidFilter?: string): void {
    if (!avoidFilter) {
      // Capture the entities included in totals before applying filters.
      let currentTotalEntities = this.homeGlobalEntities
        .filter(entity => entity.show && !entity.hidden)
        .map(entity => entity.id);
      // Apply alarm, text, and country filters.
      this.homeGlobalEntities.forEach(entity => {
        entity.hidden =
          this.checkAlarm(entity) || this.checkSearch(entity) || this.checkCountry(entity);
      });
      // Rebuild totals only when the included entity set changes.
      let newCurrentTotalEntities = this.homeGlobalEntities
        .filter(entity => entity.show && !entity.hidden)
        .map(entity => entity.id);
      if (
        currentTotalEntities.length != newCurrentTotalEntities.length ||
        currentTotalEntities.some(entity => !newCurrentTotalEntities.includes(entity))
      )
        this.updateTotals();
    }
  }

  checkAlarm(entity: GlobalEntityData): boolean {
    if (this.alarmFilter?.length > 0) {
      return !this.alarmFilter.some((filterAlarm: HomeAlarmData) =>
        entity.alarmCodeList.some(
          (alarm: HomeAlarmData) =>
            filterAlarm.alarmCode === alarm.alarmCode || filterAlarm.name === alarm.name
        )
      );
    }
    return false;
  }

  checkSearch(entity: GlobalEntityData): boolean {
    if (this.searchEntity != null && this.searchEntity != "") {
      return !entity.entity.toUpperCase().includes(this.searchEntity.toUpperCase());
    }
    return false;
  }

  checkCountry(entity: GlobalEntityData): boolean {
    if (this.countryList?.length > 1 && this.selectedCountry) {
      let clientEntity = this.entityList.find(clientEntity => clientEntity.id === entity.id);
      return clientEntity.country.toUpperCase() != this.selectedCountry.toUpperCase();
    }
    return false;
  }

  updateTotals(): void {
    this.globalEntity = null;
    this.getGlobalData();
    this.getGlobalAlarms();
  }

  updateEntitiesOrder(): void {
    this.reverseOrder = false;
    switch (this.orderBy) {
      case "entity":
        this.homeGlobalEntities.sort((a, b) => a.entity.localeCompare(b.entity));
        break;
      case "meters":
        this.homeGlobalEntities.sort((a, b) => a.devicesTotal - b.devicesTotal);
        break;
      case "alarms":
        this.homeGlobalEntities.sort((a, b) => a.totalAlarms - b.totalAlarms);
        break;
      default:
        break;
    }
  }

  updateReverseOrder(): void {
    this.homeGlobalEntities.reverse();
  }

  updateEntitiesVisibility(): void {
    this.homeGlobalEntities.forEach(entity => (entity.show = this.showAllEntities));
    this.updateTotals();
  }

  checkAllEntities(): void {
    this.showAllEntities = this.homeGlobalEntities.every(entity => entity.show);
  }
}
